import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Moment } from "moment";
import { css } from "@emotion/react";
import { DateValidationError, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import { colors, ExpandMoreIcon } from "@dotnet/telematics-design-system";

import Label from "components/DesignComponents/Label";
import LanguageContext from "context/language/languageContext";
import { isEnglish, Language, YYYY_MM_DD } from "shared";

import ClearButton from "./actionComponents/ClearButton";
import LeftArrowButton from "./actionComponents/LeftArrowButton";
import RangePickerDay from "./actionComponents/RangePickerDay";
import RightArrowButton from "./actionComponents/RightArrowButton";
import TextField from "./actionComponents/TextField";
import { DatePickerProps } from "./types";

const DatePicker = ({
    customAnchorEl,
    rangePickerValues,
    label,
    textFieldParams = {},
    customStyle,
    getError,
    onRangeDateSelect,
    ...props
}: DatePickerProps) => {
    const {
        blue800,
        blue700,
        gray100,
        gray300,
        gray700,
        gray900,
        textDisabled,
        textPlaceholder,
        white
    } = colors;

    const { languages, language } = useContext(LanguageContext);

    const { t } = useTranslation();

    const [error, setError] = useState<DateValidationError>(null);
    const [hoveredDay, setHoveredDay] = useState<Moment | null>(null);

    useEffect(() => {
        getError?.(error);
    }, [error, getError]);

    const { momentLocale } = languages.find(
        lang => lang.id === language
    ) as Language;

    const isEnglishLng = isEnglish(language);

    const adapterLocale = isEnglishLng ? "en" : momentLocale;

    const textStyles = {
        fontWeight: "500",
        lineHeight: "20px",
        letterSpacing: "0.15px"
    };

    const hasNoBuiltInInput = customAnchorEl !== undefined;

    return (
        <div
            css={css({
                ...customStyle
            })}
        >
            {!hasNoBuiltInInput && (
                <Label
                    titleLeft={label}
                    isRequired={textFieldParams.required}
                />
            )}

            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={adapterLocale}
            >
                <DesktopDatePicker
                    {...props}
                    timezone={props.timezone || "UTC"}
                    format={isEnglishLng ? YYYY_MM_DD : undefined}
                    views={["year", "month", "day"]}
                    onError={reason => setError(reason)}
                    enableAccessibleFieldDOMStructure={false}
                    slots={{
                        switchViewIcon: ExpandMoreIcon,
                        previousIconButton: LeftArrowButton,
                        nextIconButton: RightArrowButton,
                        textField: hasNoBuiltInInput ? () => null : TextField,
                        day: rangePickerValues ? RangePickerDay : undefined,
                        clearButton: ClearButton
                    }}
                    slotProps={{
                        openPickerButton: {
                            sx: {
                                color: textPlaceholder
                            }
                        },
                        field: {
                            clearable: true
                        },
                        textField: {
                            timeZoneExplanatory:
                                textFieldParams.timeZoneExplanatory,
                            tooltipProps: textFieldParams.tooltipProps,
                            helperText: error
                                ? t(`Date picker##${error}`)
                                : error
                        } as any,
                        layout: {
                            sx: {
                                "& .MuiDateCalendar-root": {
                                    width: "324px",
                                    height: "324px"
                                },

                                "& .MuiPickersCalendarHeader-root": {
                                    margin: "16px 0",
                                    padding: "0 24px",
                                    minHeight: "unset"
                                },

                                "& .MuiPickersCalendarHeader-label": {
                                    color: gray900,
                                    marginRight: "4px",
                                    textTransform: " capitalize",
                                    ...textStyles
                                },

                                // Fixes library bug when changing month
                                "& .MuiPickersCalendarHeader-label:nth-of-type(2)":
                                    {
                                        position: "absolute",
                                        top: "0",
                                        left: "0"
                                    },

                                "& .MuiPickersCalendarHeader-switchViewButton":
                                    {
                                        padding: "4px",
                                        svg: {
                                            fontSize: "16px",
                                            color: gray900
                                        }
                                    },

                                "& .MuiDayCalendar-header": {
                                    marginBottom: "4px"
                                },

                                "& .MuiPickersYear-yearButton, .MuiPickersMonth-monthButton":
                                    {
                                        color: gray700,
                                        height: "32px",
                                        margin: "4px 0",
                                        width: "calc(100% - 8px)",
                                        ...textStyles,

                                        "&.Mui-selected": {
                                            color: white,
                                            background: blue700,

                                            "&.Mui-disabled": {
                                                opacity: "0.6"
                                            }
                                        },

                                        "&.Mui-disabled:not(.Mui-selected)": {
                                            color: textDisabled,
                                            textDecoration: "line-through"
                                        },

                                        "&.Mui-selected:focus": {
                                            background: blue700
                                        },

                                        "&:focus": {
                                            background: "transparent"
                                        },

                                        "&:hover": {
                                            background: gray100
                                        }
                                    },

                                "& .MuiYearCalendar-root, .MuiMonthCalendar-root":
                                    {
                                        width: "unset",
                                        marginTop: "-4px",
                                        padding: "0 8px 12px 8px"
                                    },

                                "& .MuiYearCalendar-root": {
                                    maxHeight: "252px"
                                },

                                "& .MuiDayCalendar-weekDayLabel": {
                                    color: gray700,
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                    width: "44px",
                                    height: "unset",
                                    margin: "0 0 4px 0"
                                },

                                "& .MuiDayCalendar-weekContainer": {
                                    margin: "4px 0"
                                },

                                "& .MuiPickersDay-root": {
                                    fontSize: "16px",
                                    height: "32px",
                                    ...textStyles,
                                    ...(!rangePickerValues && {
                                        color: gray900,
                                        width: "32px",
                                        margin: "0 6px",

                                        "&.Mui-selected": {
                                            background: blue700,
                                            color: white,
                                            fontWeight: "500"
                                        },

                                        "&.Mui-selected:hover": {
                                            background: blue800
                                        },

                                        "&.Mui-selected:focus": {
                                            background: blue700
                                        },

                                        "&:focus, &.Mui-focusVisible": {
                                            outline: "none",
                                            background: "transparent"
                                        }
                                    }),

                                    "&.Mui-disabled:not(.Mui-selected)": {
                                        color: textDisabled,
                                        textDecoration: "line-through"
                                    }
                                },

                                "& .MuiPickersDay-root:not(.Mui-selected)": {
                                    borderColor: gray300
                                },

                                ...(!rangePickerValues && {
                                    "@media (pointer: fine)": {
                                        "& .MuiPickersDay-root:hover": {
                                            background: gray100
                                        }
                                    }
                                }),

                                ...(rangePickerValues && {
                                    "& .MuiPickersDay-hiddenDaySpacingFiller": {
                                        width: "42px",
                                        margin: "0"
                                    }
                                })
                            }
                        },
                        popper: {
                            ...(hasNoBuiltInInput
                                ? {
                                      anchorEl: customAnchorEl
                                  }
                                : {})
                        },
                        day: rangePickerValues
                            ? ({
                                  startDate: rangePickerValues?.startDate,
                                  endDate: rangePickerValues?.endDate,
                                  hoveredDay,
                                  setHoveredDay,
                                  onRangeDateSelect: (day: Moment) =>
                                      onRangeDateSelect?.(day)
                              } as any)
                            : undefined
                    }}
                />
            </LocalizationProvider>
        </div>
    );
};

export default DatePicker;
export { useDatePicker } from "./hooks";
